import React, { useState, useCallback, useMemo } from 'react';
import { get } from 'lodash';
import { AppealStatus, AppealType, canEdit } from '@repo/types';

import {
  Appeal,
  EditAppealApprovedPayload,
  EditAppealConfigPayload,
  EditAppealSubmittedPayload,
  EditDiscountConfigPayload,
  MatterInvoice,
  UpdateAppealStatusPayload,
} from '../../types';
import { useEditAppeal, useGetInvoice } from '../../queries';
import { pickEditModal } from '../../utils/pick-edit-modal';

import { MarkAppealSubmittedModal } from '../appeals/MarkAppealSubmitted';
import { MarkAppealApprovedModal } from '../appeals/MarkAppealApproved';
import {
  MarkToBeAppealedModal,
  type MarkToBeAppealedSubmitData,
} from '../common/to-be-appealed';
import MarkPartialWriteOffModal from '../common/MarkPartialWriteOff';

interface EditInvoiceAppealModalProps {
  invoice: MatterInvoice | null;
  onClose: () => void;
}

/**
 * Renders the edit modal chosen by `pickEditModal(appealStatus, appealType)` (the dispatch
 * table's single source of truth). The row-level `canEdit` gate at the click site keeps
 * untouched statuses out of reach.
 */
export const EditInvoiceAppealModal: React.FC<EditInvoiceAppealModalProps> = ({
  invoice,
  onClose,
}) => {
  const { mutateAsync: editAppeal } = useEditAppeal({ page: 1, limit: 50, filters: {} });
  const [isLoading, setIsLoading] = useState(false);

  const isOpen = !!invoice;
  const status = invoice?.appealStatus;
  const invoiceId = invoice?.invoiceId ?? '';
  const invoiceNumber = invoice?.invoiceNumber ?? '';

  const { data: invoiceFresh } = useGetInvoice(invoiceId, { enabled: isOpen });
  const invoiceUpdatedAt = get(invoiceFresh, 'updatedAt') as string | undefined;
  const invoiceAppealType = get(invoiceFresh, 'appeal.appealType') as AppealType | undefined;
  const effectiveAppealType = invoiceAppealType ?? invoice?.appealType;

  const basicInformation = useMemo<Partial<Appeal> | undefined>(() => {
    if (!invoice) return undefined;
    return {
      matterDisplayNumber: invoice.matterDisplayNumber ?? '',
      clientName: invoice.clientName,
      invoiceNumber: invoice.invoiceNumber,
      invoiceDate: invoice.invoiceDate,
      totalAmount: invoice.total,
      amountDue: invoice.outstandingBalance,
      paid: invoice.paid,
      creditsIssued: invoice.creditsIssued,
      responsibleAttorneyName: invoice.responsibleAttorneyName,
      appealedFees: invoice.appealedFees,
      appealedExpenses: invoice.appealedExpenses,
    };
  }, [invoice]);

  const handleEditSubmittedSubmit = useCallback(
    async (payload: Partial<UpdateAppealStatusPayload>) => {
      const expectedUpdatedAt = invoiceUpdatedAt ?? invoice?.updatedAt;
      if (!expectedUpdatedAt) {
        return;
      }
      setIsLoading(true);
      try {
        const editPayload: EditAppealSubmittedPayload = {
          expectedUpdatedAt,
          appealedFees: Number(payload.appealedFees ?? 0),
          appealedExpenses: Number(payload.appealedExpenses ?? 0),
          comment: payload.comment,
          attachments: payload.files,
        };
        await editAppeal({ invoiceId, payload: editPayload });
        onClose();
      } finally {
        setIsLoading(false);
      }
    },
    [editAppeal, invoiceId, invoiceUpdatedAt, invoice?.updatedAt, onClose]
  );

  const handleEditConfigSubmit = useCallback(
    async (data: MarkToBeAppealedSubmitData) => {
      const expectedUpdatedAt = invoiceUpdatedAt ?? invoice?.updatedAt;
      if (!expectedUpdatedAt) {
        return;
      }
      setIsLoading(true);
      try {
        const editPayload: EditAppealConfigPayload = {
          expectedUpdatedAt,
          feesCut: data.feesCut,
          expensesCut: data.expensesCut,
          promptPayment: data.promptPayment,
          bulkDiscount: data.bulkDiscount,
          administrativeFee: data.administrativeFee,
          timekeepers: data.timekeepers ?? [],
          deadline: data.deadline,
          attyDeadline: data.attyDeadline,
          comment: data.comment,
          // APPEAL_SUBMITTED+BACKLOG-only; TBA / AAS handlers ignore these server-side.
          appealedFees: data.appealedFees,
          appealedExpenses: data.appealedExpenses,
        };
        await editAppeal({ invoiceId, payload: editPayload });
        onClose();
      } finally {
        setIsLoading(false);
      }
    },
    [editAppeal, invoiceId, invoiceUpdatedAt, invoice?.updatedAt, onClose]
  );

  const handleEditDiscountConfigSubmit = useCallback(
    async (payload: UpdateAppealStatusPayload) => {
      const expectedUpdatedAt = invoiceUpdatedAt ?? invoice?.updatedAt;
      if (!expectedUpdatedAt) {
        return;
      }
      setIsLoading(true);
      try {
        const editPayload: EditDiscountConfigPayload = {
          expectedUpdatedAt,
          feesCut: Number(payload.feesCut ?? 0),
          expensesCut: Number(payload.expensesCut ?? 0),
          promptPayment: Number(payload.promptPayment ?? 0),
          bulkDiscount: Number(payload.bulkDiscount ?? 0),
          administrativeFee: Number(payload.administrativeFee ?? 0),
          comment: payload.comment,
        };
        await editAppeal({ invoiceId, payload: editPayload });
        onClose();
      } finally {
        setIsLoading(false);
      }
    },
    [editAppeal, invoiceId, invoiceUpdatedAt, invoice?.updatedAt, onClose]
  );

  const handleEditApprovedSubmit = useCallback(
    async (payload: Partial<UpdateAppealStatusPayload>) => {
      const expectedUpdatedAt = invoiceUpdatedAt ?? invoice?.updatedAt;
      if (!expectedUpdatedAt) {
        return;
      }
      setIsLoading(true);
      try {
        const editPayload: EditAppealApprovedPayload = {
          expectedUpdatedAt,
          approvedFees: Number(payload.approvedFees ?? 0),
          approvedExpenses: Number(payload.approvedExpenses ?? 0),
          approvedPromptPayment: Number(payload.approvedPromptPayment ?? 0),
          finalAppeal: payload.finalAppeal,
          comment: payload.comment,
          attachments: payload.files,
        };
        await editAppeal({ invoiceId, payload: editPayload });
        onClose();
      } finally {
        setIsLoading(false);
      }
    },
    [editAppeal, invoiceId, invoiceUpdatedAt, invoice?.updatedAt, onClose]
  );

  if (!invoice || !status) return null;
  if (!canEdit(status, effectiveAppealType)) return null;

  const choice = pickEditModal(status, effectiveAppealType);

  if (choice === 'EditAppealModal') {
    return (
      <MarkAppealSubmittedModal
        isOpen={isOpen}
        invoiceId={invoiceId}
        invoiceNumber={invoiceNumber}
        selectedStatus={AppealStatus.APPEAL_SUBMITTED}
        isLoading={isLoading}
        isEditMode
        basicInformation={basicInformation}
        onClose={onClose}
        onSubmit={handleEditSubmittedSubmit}
      />
    );
  }

  if (choice === 'MarkPartialWriteOffModal') {
    return (
      <MarkPartialWriteOffModal
        isOpen={isOpen}
        invoiceId={invoiceId}
        invoiceNumber={invoiceNumber}
        isLoading={isLoading}
        isEditMode
        targetStatus={status}
        setLoading={loading => setIsLoading(loading.discountOnly)}
        onSubmit={handleEditDiscountConfigSubmit}
        onClose={onClose}
      />
    );
  }

  if (choice === 'MarkAppealApprovedModal') {
    return (
      <MarkAppealApprovedModal
        isOpen={isOpen}
        invoiceId={invoiceId}
        invoiceNumber={invoiceNumber}
        selectedStatus={AppealStatus.APPEAL_APPROVED}
        isLoading={isLoading}
        isEditMode
        basicInformation={basicInformation}
        onClose={onClose}
        onSubmit={handleEditApprovedSubmit}
      />
    );
  }

  return (
    <MarkToBeAppealedModal
      isOpen={isOpen}
      invoiceId={invoiceId}
      invoiceNumber={invoiceNumber}
      isLoading={isLoading}
      isEditMode
      onSubmit={handleEditConfigSubmit}
      onClose={onClose}
    />
  );
};

export default EditInvoiceAppealModal;
